"use client";

import { useState, useEffect, useRef } from "react";
import { TranslationPanel } from "@/components/admin/TranslationPanel";
import Image from "next/image";
import { X, ChevronDown, ImagePlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext/AuthContext";
import { useResourceStore } from "@/store/resourceStore";
import { Resource } from "@/types";
import { ActivityLogger } from "@/lib/activityLog";
import {
  getImageUploadCopy,
  normalizeImageUpload,
  validateAvatarUploadFile,
} from "@/lib/image-upload";
import { useLanguage } from "@/context/LanguageContext";
import { getResourceCategoryLabelI18n, sortByLabel } from "@/lib/taxonomy-labels";
import { getStrapiImageUrl } from "@/lib/image-utils";

interface CreateResourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResult?: (success: boolean, message: string) => void;
  editResource?: Resource | null;
}

const MAX_TITLE_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 200;
// Mirrors the cms/server/.../resource/schema.json `phone.maxLength` constraint.
const MAX_PHONE_LENGTH = 32;

export default function CreateResourceModal({ isOpen, onClose, onResult, editResource }: CreateResourceModalProps) {
  const { currentUser } = useAuth();
  const { createResource, updateResource } = useResourceStore();
  const availableCategories = useResourceStore((s) => s.categories);
  const fetchResourceCategories = useResourceStore((s) => s.fetchCategories);

  useEffect(() => {
    if (isOpen) {
      void fetchResourceCategories();
    }
  }, [isOpen, fetchResourceCategories]);

  /**
   * The parent keeps this modal MOUNTED while closed, so state it produced has to be
   * cleared on deactivation -- otherwise pending Spanish edits for one resource would be
   * carried into the next resource opened.
   */
  useEffect(() => {
    if (!isOpen) setTranslationEdits({});
  }, [isOpen]);
  const { t, language } = useLanguage();
  const logoCopy = getImageUploadCopy(language, 'avatar');

  const [title, setTitle] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  /** MWBI-900 dirty-tracking: only fields the admin actually edited are sent. */
  const [translationEdits, setTranslationEdits] = useState<Record<string, string | boolean>>({});
  const [spanishTitle, setSpanishTitle] = useState("");
  const [spanishDescription, setSpanishDescription] = useState("");
  const [resourceLink, setResourceLink] = useState("");
  const [phone, setPhone] = useState("");
  const [selectedCategorySlugs, setSelectedCategorySlugs] = useState<string[]>([]);
  const [isFeatured, setIsFeatured] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  // Sticky flag so a user who clicks X on an existing logo (without picking a
  // replacement) can persist that intent — store sends logo: null only when set.
  const [logoCleared, setLogoCleared] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  // Tracks the latest blob: URL so the unmount cleanup revokes the *current*
  // value — a useEffect with [] deps captures the mount-time render's state
  // and would not see later picks.
  const logoObjectUrlRef = useRef<string | null>(null);

  const revokeCurrentObjectUrl = () => {
    if (logoObjectUrlRef.current) {
      URL.revokeObjectURL(logoObjectUrlRef.current);
      logoObjectUrlRef.current = null;
    }
  };

  const resetForm = () => {
    setTitle("");
    setShortDescription("");
    setResourceLink("");
    setPhone("");
    setSelectedCategorySlugs([]);
    setIsFeatured(false);
    revokeCurrentObjectUrl();
    setLogoFile(null);
    setLogoPreview(null);
    setLogoError(null);
    setLogoCleared(false);
  };

  useEffect(() => {
    if (editResource) {
      setTitle(editResource.title);
      setShortDescription(editResource.shortDescription);
      setTranslationEdits({});
      setSpanishTitle(editResource.titleEs ?? "");
      setSpanishDescription(editResource.shortDescriptionEs ?? "");
      // Legacy rows put the phone number into resourceLink as "tel:...". Always
      // strip a tel: prefix out of the link field — phone wins if present, and
      // otherwise the legacy tel: becomes the new phone — so editors never see
      // (or accidentally re-save) the denormalized tel: URL.
      const legacyLink = editResource.resourceLink ?? "";
      if (legacyLink.startsWith("tel:")) {
        setResourceLink("");
        setPhone(editResource.phone ?? legacyLink.slice(4));
      } else {
        setResourceLink(legacyLink);
        setPhone(editResource.phone ?? "");
      }
      // editResource.categories is the relation array of records; pull slugs.
      setSelectedCategorySlugs(editResource.categories.map((c) => c.slug));
      setIsFeatured(editResource.isFeatured);
      // Seed preview from the existing remote URL (not an object URL — do not revoke).
      revokeCurrentObjectUrl();
      setLogoFile(null);
      setLogoCleared(false);
      setLogoError(null);
      setLogoPreview(editResource.logo?.url ? (getStrapiImageUrl(editResource.logo.url) ?? null) : null);
      setShowCategoryDropdown(false);
    } else {
      resetForm();
      setShowCategoryDropdown(false);
    }
    // logoFile/logoPreview deliberately omitted to avoid clobbering a user's
    // in-flight pick while the same modal is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editResource, isOpen]);

  // Revoke the last object URL on unmount — reads the ref so it always sees
  // the latest blob URL, not the mount-time render's state.
  useEffect(() => {
    return () => {
      if (logoObjectUrlRef.current) URL.revokeObjectURL(logoObjectUrlRef.current);
    };
  }, []);

  const handleLogoFile = async (file: File) => {
    const validationError = validateAvatarUploadFile(file, language);
    if (validationError) { setLogoError(validationError); return; }
    const normalized = await normalizeImageUpload(file);
    revokeCurrentObjectUrl();
    const objectUrl = URL.createObjectURL(normalized);
    logoObjectUrlRef.current = objectUrl;
    setLogoError(null);
    setLogoCleared(false);
    setLogoFile(normalized);
    setLogoPreview(objectUrl);
    if (logoInputRef.current) logoInputRef.current.value = "";
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleLogoFile(file);
  };

  const handleDropLogo = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) void handleLogoFile(file);
  };

  const removeLogo = () => {
    revokeCurrentObjectUrl();
    setLogoFile(null);
    setLogoPreview(null);
    setLogoError(null);
    setLogoCleared(true);
  };

  if (!isOpen) return null;

  const handleCategoryToggle = (slug: string) => {
    setSelectedCategorySlugs((currentSlugs) => {
      if (currentSlugs.includes(slug)) {
        return currentSlugs.filter((current) => current !== slug);
      }

      if (currentSlugs.length >= 4) {
        return currentSlugs;
      }

      return [...currentSlugs, slug];
    });
  };

  const handleSpanishChange = (name: string, value: string) => {
    if (name === "titleEs") setSpanishTitle(value);
    if (name === "shortDescriptionEs") setSpanishDescription(value);
    setTranslationEdits((prev) => ({ ...prev, [name]: value }));
  };

  /** Sends both re-arm signals: clear the lock and blank the Spanish. */
  const handleResetTranslation = () => {
    setSpanishTitle("");
    setSpanishDescription("");
    setTranslationEdits({ titleEs: "", shortDescriptionEs: "", translationLocked: false });
  };

  const handleSubmit = async () => {
    const trimmedLink = resourceLink.trim();
    const trimmedPhone = phone.trim();
    if (
      !currentUser ||
      !title.trim() ||
      !shortDescription.trim() ||
      (!trimmedLink && !trimmedPhone) ||
      isSubmitting
    ) return;

    setIsSubmitting(true);

    try {
      // null clears the field on an update (Strapi semantics); on create the
      // value is irrelevant either way for an optional field. Matches the
      // pre-MWBI-799 behaviour the tests assert.
      const data = {
        title: title.trim(),
        shortDescription: shortDescription.trim(),
        resourceLink: trimmedLink || null,
        phone: trimmedPhone || null,
        categorySlugs: selectedCategorySlugs,
        isFeatured,
      };

      // Logo file semantics for the store: File = upload + set, null = clear, undefined = no change.
      const logoArg: File | null | undefined = logoFile
        ? logoFile
        : (logoCleared && editResource ? null : undefined);

      let success;
      if (editResource) {
        const result = await updateResource(
          editResource.documentId,
          { ...data, ...translationEdits },
          logoArg,
        );
        success = !!result;
        if (result) ActivityLogger.editResource(currentUser, data.title, editResource.documentId);
      } else {
        const result = await createResource(data, logoArg);
        success = !!result;
        if (result) ActivityLogger.createResource(currentUser, data.title, result.documentId);
      }

      if (success) {
        onResult?.(true, editResource ? 'Resource updated successfully' : 'Resource created successfully');
        resetForm();
        onClose();
      } else {
        onResult?.(false, 'Failed to save resource');
      }
    } catch (error) {
      console.error('src/components/ui/CreateResourceModal.tsx', error);
      onResult?.(false, 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid =
    title.trim().length > 0 &&
    title.length <= MAX_TITLE_LENGTH &&
    shortDescription.trim().length > 0 &&
    shortDescription.length <= MAX_DESCRIPTION_LENGTH &&
    (resourceLink.trim().length > 0 || phone.trim().length > 0) &&
    selectedCategorySlugs.length > 0;

  const selectedCategoryLabels = selectedCategorySlugs
    .map((slug) => {
      const option = availableCategories.find((c) => c.slug === slug);
      return option ? getResourceCategoryLabelI18n(t, option.slug, option.name) : slug;
    })
    .join(', ');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div data-testid="create-resource-root" className="relative bg-background rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-background z-10">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {editResource ? 'Edit Resource' : 'Create Resource'}
            </h2>
          </div>
          <button data-testid="create-resource-close-btn" onClick={onClose} className="p-1 hover:bg-sidebar-hover rounded transition">
            <X size={20} className="text-foreground-muted" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Avatar */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-background-secondary flex items-center justify-center overflow-hidden">
              {currentUser?.avatarUrl ? (
                <img src={currentUser.avatarUrl} alt={currentUser.displayName} className="w-full h-full object-contain" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-foreground font-medium" style={{ backgroundColor: currentUser?.avatarColor || '#ccc' }}>
                  {currentUser?.initials || 'U'}
                </div>
              )}
            </div>
            <div>
              <p className="font-medium text-foreground">{currentUser?.displayName || currentUser?.username}</p>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Title *</label>
            <input
              data-testid="create-resource-title-input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={MAX_TITLE_LENGTH}
              placeholder="Enter resource title"
              className="w-full px-3 py-2 border border-border rounded-md text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="text-xs text-caption mt-1">{title.length} / {MAX_TITLE_LENGTH}</p>
          </div>

          {/* Short Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Short Description *</label>
            <textarea
              data-testid="create-resource-description-textarea"
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
              maxLength={MAX_DESCRIPTION_LENGTH}
              placeholder="Brief description of the resource..."
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-md text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
            <p className="text-xs text-caption mt-1">{shortDescription.length} / {MAX_DESCRIPTION_LENGTH}</p>
          </div>

          {/* Featured Checkbox */}
          <div className="flex items-center gap-3">
            <input
              data-testid="create-resource-featured-checkbox"
              type="checkbox"
              id="featured"
              checked={isFeatured}
              onChange={(e) => setIsFeatured(e.target.checked)}
              className="w-4 h-4 rounded border-border"
            />
            <label htmlFor="featured" className="text-sm text-foreground">
              Featured resource
            </label>
          </div>

          {/* Categories */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Categories (Select up to 4) *</label>
            <div className="relative">
              <button
                data-testid="create-resource-category-dropdown-btn"
                type="button"
                onClick={() => setShowCategoryDropdown(currentState => !currentState)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-left text-sm text-foreground flex items-center justify-between gap-3 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <span className={selectedCategorySlugs.length > 0 ? 'text-foreground' : 'text-foreground-muted'}>
                  {selectedCategorySlugs.length > 0 ? selectedCategoryLabels : 'Select categories'}
                </span>
                <ChevronDown
                  size={16}
                  className={`text-foreground-muted transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`}
                />
              </button>

              {showCategoryDropdown && (
                <div data-testid="create-resource-category-dropdown" className="absolute z-20 mt-2 w-full rounded-md border border-border bg-background shadow-lg max-h-60 overflow-y-auto p-2">
                  <div className="space-y-1">
                    {sortByLabel(
                      availableCategories.filter((cat) => !cat.archived),
                      (cat) => getResourceCategoryLabelI18n(t, cat.slug, cat.name),
                      language,
                    ).map((cat) => (
                        <label key={cat.slug} className="flex items-center gap-2 cursor-pointer hover:bg-sidebar-hover p-2 rounded text-sm">
                          <input
                            data-testid={`create-resource-category-checkbox-${cat.slug}`}
                            type="checkbox"
                            checked={selectedCategorySlugs.includes(cat.slug)}
                            onChange={() => handleCategoryToggle(cat.slug)}
                            disabled={!selectedCategorySlugs.includes(cat.slug) && selectedCategorySlugs.length >= 4}
                            className="w-4 h-4 rounded border-border"
                          />
                          <span className="text-foreground">{getResourceCategoryLabelI18n(t, cat.slug, cat.name)}</span>
                        </label>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {selectedCategorySlugs.length > 0 && (
              <p className="text-xs text-foreground-muted mt-2">Selected: {selectedCategoryLabels}</p>
            )}
            <p className="text-xs text-caption mt-1">{selectedCategorySlugs.length} of 4 categories selected</p>
          </div>

          {/* Logo */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Logo</label>
            <div
              onDrop={handleDropLogo}
              onDragOver={(e) => e.preventDefault()}
            >
              {logoPreview ? (
                <div className="relative w-32 h-32 rounded-md overflow-hidden border border-border bg-background-secondary mx-auto">
                  <Image src={logoPreview} alt="Resource logo" fill className="object-cover" unoptimized />
                  <button
                    type="button"
                    onClick={removeLogo}
                    data-testid="create-resource-logo-remove-btn"
                    aria-label="Remove logo"
                    className="absolute top-1 right-1 bg-error text-white rounded-full p-0.5"
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  data-testid="create-resource-logo-drop-zone-btn"
                  className="w-full h-32 rounded-md border border-dashed border-border bg-background-secondary flex flex-col items-center justify-center gap-1.5 hover:bg-sidebar-hover transition"
                >
                  <ImagePlus size={28} className="text-foreground-muted" />
                  <span className="text-sm text-foreground-muted">{logoCopy.uploadPrompt}</span>
                </button>
              )}
            </div>
            {logoPreview && (
              <div className="flex justify-center mt-2">
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  className="text-xs text-primary hover:underline"
                >
                  {logoCopy.uploadPrompt}
                </button>
              </div>
            )}
            <p className="text-xs text-foreground-muted mt-1.5 text-center">{logoCopy.fileTypes}</p>
            {logoError && <p className="text-xs text-error mt-1 text-center">{logoError}</p>}
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleLogoChange}
              data-testid="create-resource-logo-input"
              className="hidden"
            />
          </div>

          {/* Spanish (auto-translated) — edit mode only */}
          {editResource && (
            <TranslationPanel
              isActive={isOpen}
              disabled={isSubmitting}
              status={editResource.translationStatus}
              locked={
                typeof translationEdits.translationLocked === "boolean"
                  ? translationEdits.translationLocked
                  : editResource.translationLocked
              }
              testIdPrefix="resource-translation"
              fields={[
                { name: "titleEs", label: "Title", value: spanishTitle },
                {
                  name: "shortDescriptionEs",
                  label: "Short Description",
                  value: spanishDescription,
                  multiline: true,
                },
              ]}
              onChange={handleSpanishChange}
              onResetToAutomatic={handleResetTranslation}
            />
          )}

          {/* Resource Link */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Resource Link</label>
            <input
              data-testid="create-resource-link-input"
              type="url"
              value={resourceLink}
              onChange={(e) => setResourceLink(e.target.value)}
              placeholder="https://example.com"
              className="w-full px-3 py-2 border border-border rounded-md text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Phone</label>
            <input
              data-testid="create-resource-phone-input"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              maxLength={MAX_PHONE_LENGTH}
              placeholder="988 or 1-800-555-1234"
              className="w-full px-3 py-2 border border-border rounded-md text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="text-xs text-caption mt-1">{phone.length} / {MAX_PHONE_LENGTH}</p>
            <p className="text-xs text-caption mt-1">Provide a website link, a phone number, or both.</p>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-border sticky bottom-0 bg-background">
          <button
            data-testid="create-resource-cancel-btn"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-foreground hover:bg-sidebar-hover rounded-md transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid="create-resource-submit-btn"
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-foreground rounded-md hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Saving...' : (editResource ? 'Update Resource' : 'Create Resource')}
          </button>
        </div>
      </div>
    </div>
  );
}


